import type { PillItem } from "@search/api/types";
import { LocationValue } from "@search/store/types";

import { toSnakeCase } from "@/utils/helpers";

type SerializedBase = Record<string, FilterValue>;

type BaseFilterType = {
  defaultValue?: FilterValue;
  label?: string;
  name: string;
  value: FilterValue;
};
type FilterValue = boolean | number | string | Date | LocationValue;

interface IBaseFilter {
  get: () => FilterValue;
  isSet: () => boolean;
  label: string;
  name: string;
  remove: () => SerializedBase;
  serialize: () => SerializedBase;
  set: (value: FilterValue) => SerializedBase;
  toParam: () => string;
  toPills: () => Array<PillItem>;
  toURL: () => Record<string, string>;
  unset: () => SerializedBase;
  value: FilterValue;
}

class BaseFilter implements IBaseFilter {
  private _label: string;
  private _name: string;

  protected _defaultValue?: FilterValue;
  protected _initialValue: FilterValue;
  protected _value: FilterValue;

  constructor({
    defaultValue,
    name = "base",
    label = name,
    value,
  }: BaseFilterType) {
    this._defaultValue = defaultValue;
    this._initialValue = value;
    this._label = label;
    this._name = name;
    this._value = value;
  }

  get defaultValue() {
    return this._defaultValue;
  }

  get initialValue() {
    return this._initialValue;
  }

  get label() {
    return this._label;
  }

  get name() {
    return this._name;
  }

  get value() {
    return this._value;
  }

  get() {
    return this.value;
  }

  isSet() {
    return this._value != null && this._value !== this._defaultValue;
  }

  remove() {
    return this.set(this.initialValue);
  }

  serialize(): SerializedBase {
    return { [this.name]: this.value };
  }

  set(value: FilterValue) {
    this._value = value;

    return this.serialize();
  }

  toParam() {
    return this._value.toString();
  }

  toPills(): Array<PillItem> {
    const pills: Array<PillItem> = [];

    if (this.isSet()) {
      pills.push({
        key: this.label,
        name: this.name,
      });
    }

    return pills;
  }

  toURL(): Record<string, string> {
    if (this.isSet()) {
      return { [toSnakeCase(this._name)]: this.toParam() };
    }

    return {};
  }

  unset() {
    return this.set(this.defaultValue ?? this.initialValue);
  }
}

export default BaseFilter;
export { BaseFilter };
export type { BaseFilterType, FilterValue, IBaseFilter };
